import { RightShipInspectionHelper } from './../../../../../../helpers/rightship-inspection-helper';
import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { ChildComponent } from "../../../child-component";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Reflection } from "../../../../../../utilities/reflection";
import { RightShipInspectionClientModel, RightShipInspectionStatusEnum, type RightShipInspectionStatusClientModel, RightShipInspectionType, RightshipInspectionBillingStatusEnum } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Assignment } from "../../../../../components/assignment/assignment";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { ClipboardCopyService } from "../../../../../../utilities/clipboard-copy-service";

@autoinject
@transient()
@useView('views/pages/modules/rightship-inspection/steps/overview/rightship-inspection-overview.html')
export class RightShipInspectionOverview extends ChildComponent<RightShipInspectionClientModel> {

    reassignment: RightShipInspectionStatusEnum = RightShipInspectionStatusEnum.RightShipInspectionReassignment;
    assignment: Assignment;
    rightShipUser: boolean;
    canViewInspectionGradingBadge: boolean;
    canEditUpdateRightshipInspection: boolean;
    canEditUpdateTankerInspection: boolean;
    canChangeInspectionCompany: boolean;
    canChangeRSInspectionInspector: boolean;
    canModifyBillingDetails: boolean;
    canEditBillingDetailsCurrencyAmountSales: boolean;
    changeRightShipInspectionCompany: () => void;
    changeRightShipInspectionInspector: () => void;

    @computedFrom('item.inspectionTypeId')
    get isCargill() {
        return this.item && this.item.inspectionTypeId == RightShipInspectionType.CargillSurvey;
    }
    
    @computedFrom('item.inspectionTypeId')
    get isWet() {
        return this.item && this.item.inspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom('item.inspectionTypeId')
    get isDry() {
        return this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection;
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.isCargill),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.reasonForInspection),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.port),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.requestedDateOfInspection),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.actualDateOfInspection),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.billingCustomer))
    get showInfo() {
        if (this.isCargill) return false;

        return !this.item.reasonForInspection
            || !this.item.port || (!this.item.requestedDateOfInspection && !this.item.actualDateOfInspection) || 
            !this.item.invoice || !this.item.invoice.billingAccountId;
    }

    @computedFrom("currentStatus.statusId")
    public get isCanceled(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Cancelled;
    }

    @computedFrom("currentStatus.statusId")
    public get isClosed(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Acceptable || this.currentStatus.statusId == RightShipInspectionStatusEnum.Unacceptable;
    }

    @computedFrom("currentStatus.statusId")
    public get isAcceptable(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Acceptable;
    }

    @computedFrom("currentStatus.inspectionTypeId")
    public get inspectingCompanyParams(): string {
        return `${this.isCargill ? '1' : '0'},${this.isWet ? '1' : '0'}`;
    }
    @computedFrom("currentStatus.statusId")
    public get isUnacceptable(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Unacceptable;
    }

    @computedFrom("item.statuses.length")
    public get currentStatus(): RightShipInspectionStatusClientModel {
        if (this.item && this.item.statuses && this.item.statuses.length > 0) {
            return this.item.statuses[0];
        } else return null;
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.isClosed))
    get mainReason() {
        return this.isClosed ? null : this.currentStatus.subtaskTypes.join(", ");
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item), Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item.vesselDateOfBuild))
    public get age() {
        if (this.item) {
            if (this.item.vesselDateOfBuild) {
                return (Math.floor(moment().diff(this.item.vesselDateOfBuild, 'year', true) * 10) / 10).toFixed(1) + "y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.cargillInspectionDetails))
    get totalRiskRating() {
        if (this.item && this.item.cargillInspectionDetails && this.item.cargillInspectionDetails) {
            const item = this.item.cargillInspectionDetails;

            const result = (item.certificationAndDocumentationProbability * item.certificationAndDocumentationSeverity) +
                (item.crewManagementProbability * item.crewManagementSeverity) +
                (item.navigationProbability * item.navigationSeverity) +
                (item.safetyManagementProbability * item.safetyManagementSeverity) +
                (item.pollutionManagementProbability * item.pollutionManagementSeverity) +
                (item.machinerySpacesProbability * item.machinerySpacesSeverity) +
                (item.hullAndDeckItemsProbability * item.hullAndDeckItemsSeverity) +
                (item.cargoAndBallastSystemsProbability * item.cargoAndBallastSystemsSeverity) +
                (item.accommodationProbability * item.accommodationSeverity);

            return result.toFixed(2);
        }
        return "N/A";
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item), Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item.additionalEmailsToNotify))
    get additionalEmailsToNotify() {
        if (this.item) {
            return this.getEmailsAsList(this.item.additionalEmailsToNotify);
        }
        return [];
    }

    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item), 
        Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item.invoice),
        Reflection.getPropertyPath<RightShipInspectionOverview>(x => x.item.invoice.additionalEmailsToNotify))
    get invoiceEmails() {
        if (this.item && this.item.invoice && this.item.invoice.additionalEmailsToNotify) {
            return this.getEmailsAsList(this.item.invoice.additionalEmailsToNotify);
        }
        return [];
    }

    constructor(public inspectionHelper : RightShipInspectionHelper, notificationsService: NotificationClientService,
        i18N: I18N, private router: Router, private dialogService: DialogClientService, private clipboardCopyService: ClipboardCopyService, userClaimService: UserClaimsService,
        protected signalClientService: SignalClientService) {
        super(notificationsService, i18N);

        this.rightShipUser = userClaimService.hasPermission(Claims.RightShipUser);
        this.canEditUpdateRightshipInspection = userClaimService.hasPermission(Claims.CanEditUpdateRightShipInspection);
        this.canEditUpdateTankerInspection = userClaimService.hasPermission(Claims.CanEditUpdateTankerInspection);
        this.canChangeInspectionCompany = userClaimService.hasPermission(Claims.CanChangeInspectionCompany);
        this.canChangeRSInspectionInspector = userClaimService.hasPermission(Claims.CanChangeRSInspectionInspector);
        this.canModifyBillingDetails = userClaimService.hasPermission(Claims.CanModifyBillingDetails);
        this.canEditBillingDetailsCurrencyAmountSales = userClaimService.hasPermission(Claims.CanEditBillingDetailsCurrencyAmountSales);
        this.canViewInspectionGradingBadge = userClaimService.hasPermission(Claims.CanViewInspectionGradingBadge);
    }

    activate(params: any) {
        this.assignment = params.assignment;
        return super.activate(params.item);
    }

    attached() {
        this.signalClientService.subscribe(SignalFunctions.refreshInspectionTabletSyncStatus, this.refreshInspectionTabletSyncStatus);
        this.signalClientService.subscribe(SignalFunctions.rightShipInspectionCompanyChanged, this.rightShipInspectionCompanyChanged);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.refreshInspectionTabletSyncStatus, this.refreshInspectionTabletSyncStatus);
        this.signalClientService.unsubscribe(SignalFunctions.rightShipInspectionCompanyChanged, this.rightShipInspectionCompanyChanged);
    }

    refreshInspectionTabletSyncStatus = (param) => {
        if (param.inspectionId == this.item.rightShipInspectionId) {
            this.item['tabletSyncStatus'] = param.tabletSyncStatus;
            this.item['tabletLastSyncDate'] = param.tabletLastSyncDate;
            this.item['tabletSyncStatusError'] = param.tabletSyncStatusError;
        }
    }

    rightShipInspectionCompanyChanged = (param) => {
        if (param.rightShipInspectionId == this.item.rightShipInspectionId) {
            this.item.inspectingCompanyId = param.inspectingCompanyId;
        }
    }

    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    @computedFrom('canEditUpdateRightshipInspection', 'canEditUpdateTankerInspection', 'item.inspectionTypeId')
    get showUpdate() {        
        return this.item && 
            (this.canEditUpdateRightshipInspection && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection) ||
            (this.canEditUpdateTankerInspection && this.item.inspectionTypeId == RightShipInspectionType.TankerInspection) ||
            (this.item.inspectionTypeId == RightShipInspectionType.CargillSurvey);
    }

    // Show edit icon for billing details
    @computedFrom('item.invoice', 'canModifyBillingDetails', 'item.billingStatusId')
    get showEditBillingDetails() {  
        return this.item && this.item.invoice && this.canModifyBillingDetails && 
        this.item.billingStatusId != RightshipInspectionBillingStatusEnum.SalesOrderCreated && 
        this.item.billingStatusId != RightshipInspectionBillingStatusEnum.SalesOrderInitiated;
    }

    // Total amount to be billed to customer
    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.totalSupplierInvoiceAmountUSD),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.inspectionMarkup),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.cancelledInspectionPenalty),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.lateInspectionPenalty),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.otherPenaltyAmount))
    get totalAmount(): number {
        let result: number = 0;
        if(this.item.invoice.totalSupplierInvoiceAmountUSD > 0) result += this.item.invoice.totalSupplierInvoiceAmountUSD;
        if(this.item.invoice.inspectionMarkup > 0) result += this.item.invoice.inspectionMarkup;
        if(this.item.invoice.cancelledInspectionPenalty > 0) result += this.item.invoice.cancelledInspectionPenalty;
        if(this.item.invoice.lateInspectionPenalty > 0) result += this.item.invoice.lateInspectionPenalty;
        if(this.item.invoice.otherPenaltyAmount > 0) result += this.item.invoice.otherPenaltyAmount;
        return result;
    }

    // Total supplier invoice amount (USD)
    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.supplierTravelAndExpenseAmount),
    Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.supplierTravelAndExpenseAmountUSD),
    Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.currencyConversionRate))    
    get totalSupplierInvoiceAmountUSD(): number {
        if(this.totalInvoiceAmount > 0){
            this.item.invoice.totalSupplierInvoiceAmountUSD =  this.totalInvoiceAmount*this.item.invoice.currencyConversionRate;
            return this.item.invoice.totalSupplierInvoiceAmountUSD;
        }
        return 0;
    }

    // Supplier Travel & Expense Amount (USD)
    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.supplierTravelAndExpenseAmount),
    Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.supplierTravelAndExpenseAmountUSD),
    Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.currencyConversionRate))
    get supplierTravelAndExpenseAmountUSD(): number {
        if(this.item.invoice.supplierTravelAndExpenseAmount > 0){
            this.item.invoice.supplierTravelAndExpenseAmountUSD = this.item.invoice.supplierTravelAndExpenseAmount*this.item.invoice.currencyConversionRate
            return this.item.invoice.supplierTravelAndExpenseAmountUSD;
        }
        return 0;
    }
    
    // Total invoice amount
    @computedFrom(Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.supplierInspectionAmount),
        Reflection.getPropertyPath<RightShipInspectionOverview>(z => z.item.invoice.supplierTravelAndExpenseAmount))
    get totalInvoiceAmount(): number {
        let result: number = 0;
        let inspectionCost = 0;
        let travelCost = 0;
        if (this.item.invoice.supplierInspectionAmount > 0) inspectionCost = +(this.item.invoice.supplierInspectionAmount) || 0;
        if (this.item.invoice.supplierTravelAndExpenseAmount > 0) travelCost = +(this.item.invoice.supplierTravelAndExpenseAmount) || 0;
        result = inspectionCost + travelCost;
        return result;
    }    

    editRightShipInspection() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddRightShipInspection,
            {
                id: this.item.rightShipInspectionId,
                inspectionTypeId: this.item.inspectionTypeId,
                edit: true
            });
    }

    showStatusComment(status: RightShipInspectionStatusClientModel) {
        if (this.isCargill)
            return status.statusId == RightShipInspectionStatusEnum.InProgress;

        if (this.isWet)
            return status.statusId == RightShipInspectionStatusEnum.InProgress ||
                status.statusId == RightShipInspectionStatusEnum.AwaitingResponse ||
                status.statusId == RightShipInspectionStatusEnum.ReviewRequired;

        return true;
    }

    showFullComment(status: RightShipInspectionStatusClientModel) {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipInspectionCommentDialog, status, true, false, false, 'right');
    }

    showStatusCommentLink(status: RightShipInspectionStatusClientModel) {
        const statuses = [
            RightShipInspectionStatusEnum.Scheduled
        ];

        return status && status.fullComment && statuses.indexOf(status.statusId) <= -1;
    }

    getEmailsAsList(emails: string) {
        let listOfEmails = [];

        if (emails != null) {
            listOfEmails = emails.split(";").map(x => {
                return { email: x };
            });
        }

        return listOfEmails;
    }

    getDateToString(date: Date | string, dateFormat: string = null): string {
        if (!date)
            return ''

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';
        if(!dateFormat)
            return moment(val).format('MMMM Do YYYY');
        else
            return moment(val).format(dateFormat);
    }

    getGradeClass(code: string)
    {
        let category = "";
        const parsedCode = parseInt(code);
        if(!isNaN(parsedCode) && isFinite(parsedCode))
        {
            const numbersText = ["", "one", "two", "three", "four", "five"];
            category = parsedCode > -1 && parsedCode < 6? numbersText[parsedCode]: "";
        }
        else category = code? code.toLowerCase(): "";

        return `class-${category}`;
    }

    editBillingDetails()
    {
        if(this.item && this.canModifyBillingDetails)
        {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.EditRSInspectionBillingDetailsDialog, 
                { rightShipInspectionId: this.item.rightShipInspectionId, invoice: this.item.invoice, item: this.item },
                true, false, false, 'right');
        }
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }
}